import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { USER_FEEDBACK } from "../../../utils/API/APIErrors";
import { AddressDetails } from "../../../utils/API/Bring/bringAPISchema";
import { updateCustomer } from "../../../utils/API/Cart/cartAPI";
import {
    AddressModelWithoutAnalytics,
    AddressModelWithoutAnalyticsSchema,
    PlaceholderAddressModel,
} from "../../../utils/API/Cart/cartAPISchema";
import { getCustomerInformation } from "../../../utils/API/Customer/customerAPI";
import { Log } from "../../../utils/API/Logging/LoggingAPI";
import { getCityFromPostalCode } from "../../../utils/API/Postal/postalAPI";
import * as CheckoutHelpers from "../../../utils/checkoutHelpers";
import { FEEDBACK_TEXT } from "../../../utils/validationHelpers";

const requiredCustomerFieldKeys: Array<keyof AddressModelWithoutAnalytics> = [
    "email", "firstName", "lastName", "phoneNumber", "address", "postalCode", "city",
];

const emptyFields = (): PlaceholderAddressModel => ({
    email: "", firstName: "", lastName: "", phoneNumber: "",
    address: "", postalCode: "", city: "", bringAddressId: "",
});

const emptyErrors = () => ({
    email: [] as string[], firstName: [] as string[], lastName: [] as string[],
    phoneNumber: [] as string[], address: [] as string[],
    postalCode: [] as string[], city: [] as string[],
});

export const useCustomerInformationStore = defineStore("customerInformation", () => {
    // State
    const fields = ref<PlaceholderAddressModel>(emptyFields());
    const errors = ref(emptyErrors());
    const submittedFields = ref<AddressModelWithoutAnalytics>({
        email: "", firstName: "", lastName: "", phoneNumber: "",
        address: "", postalCode: "", city: "", bringAddressId: "",
    });
    const retrievingCity = ref(false);
    const errorSummaryVisible = ref(false);
    const mostRecentAddressSuggestions = ref<AddressDetails[]>([]);
    // Email of the account the persisted draft belongs to; undefined for a guest-typed draft.
    const draftOwnerEmail = ref<string | undefined>(undefined);

    // Getters
    const isCustomerStepCompleted = computed(() => {
        const hasRequiredSubmittedValues = requiredCustomerFieldKeys.every((key) => {
            const value = submittedFields.value?.[key];
            return typeof value === "string" && value.trim().length > 0;
        });
        if (!hasRequiredSubmittedValues) return false;
        const hasNoFieldErrors = Object.values(errors.value).every((e) => e.length === 0);
        if (!hasNoFieldErrors) return false;
        return requiredCustomerFieldKeys.every((key) => {
            const submitted = submittedFields.value?.[key]?.trim() ?? "";
            const current = fields.value?.[key]?.trim() ?? "";
            return submitted === current;
        });
    });

    const fieldsEmpty = computed(() =>
        Object.values(fields.value).some((field) => field === ""),
    );

    const errorsExist = computed(() =>
        Object.values(errors.value).some((e) => e.length > 0),
    );

    // Actions
    function persistDraftToLocalStorage() {
        try {
            localStorage.setItem(
                'checkout_customer_draft',
                JSON.stringify({ fields: fields.value, ownerEmail: draftOwnerEmail.value }),
            );
        } catch (e) {}
    }
    function clearDraftFromLocalStorage() {
        try { localStorage.removeItem('checkout_customer_draft'); } catch (e) {}
    }
    function loadDraftFromLocalStorage() {
        try {
            const raw = localStorage.getItem('checkout_customer_draft');
            if (raw) setFields(JSON.parse(raw));
        } catch (e) {}
    }
    function hasDraftInput() {
        return Object.values(fields.value ?? {}).some(
            (value) => typeof value === "string" && value.trim().length > 0,
        );
    }
    // Runs once at store setup so a guest's draft survives a real page reload -
    // in-memory state otherwise resets to empty, unlike localStorage.
    function hydrateFromLocalStorage() {
        try {
            const raw = localStorage.getItem('checkout_customer_draft');
            if (!raw) return;
            const parsed = JSON.parse(raw);
            fields.value = parsed.fields ?? emptyFields();
            draftOwnerEmail.value = parsed.ownerEmail;
        } catch (e) {}
    }
    function hydrateFieldsIfEmpty(f: PlaceholderAddressModel | null | undefined) {
        if (hasDraftInput()) return;
        setFields(f);
    }
    function setField(field: string | number, value: string | undefined) {
        fields.value[field] = value;
        persistDraftToLocalStorage();
    }
    function setFields(f: PlaceholderAddressModel | null | undefined) {
        if (!f) { clearAllFields(); clearDraftFromLocalStorage(); return; }
        fields.value = f;
        persistDraftToLocalStorage();
    }
    function clearAllFields() {
        fields.value = emptyFields();
        draftOwnerEmail.value = undefined;
        clearDraftFromLocalStorage();
    }
    function setError(field: string | number, error: any) {
        errors.value[field] = error;
    }
    function clearError(field: string | number) {
        errors.value[field] = [];
    }
    function clearAllErrors() {
        errors.value = emptyErrors();
    }
    function setSubmittedFields(f: AddressModelWithoutAnalytics) {
        submittedFields.value = f;
    }
    function setErrorSummaryVisible(visible: boolean) {
        errorSummaryVisible.value = visible;
    }
    function setRetrievingCity(retrieving: boolean) {
        retrievingCity.value = retrieving;
    }
    function setMostRecentAddressSuggestions(suggestions: AddressDetails[]) {
        mostRecentAddressSuggestions.value = suggestions;
    }
    function clear() {
        clearAllFields();
        clearAllErrors();
        submittedFields.value = {
            email: "", firstName: "", lastName: "", phoneNumber: "",
            address: "", postalCode: "", city: "", bringAddressId: "",
        };
        retrievingCity.value = false;
        errorSummaryVisible.value = false;
        clearDraftFromLocalStorage();
    }

    async function setFieldsIfLoggedIn(): Promise<void> {
        try {
            const data = await getCustomerInformation();
            if (!data) {
                // Anonymous: a draft tagged to a previous account must not leak to this visitor.
                if (draftOwnerEmail.value) clearAllFields();
                return;
            }
            for (const key in data) { if (data[key] === null) data[key] = ''; }
            if (data.email === draftOwnerEmail.value) return;
            draftOwnerEmail.value = data.email;
            setFields(data);
        } catch (error) {
            Log({
                LogLevel: 'Warning',
                Message: `[customerInformationStore] Failed to get customer information: ${error instanceof Error ? error.message : String(error)}`,
            });
        }
    }

    async function submitForm(): Promise<boolean> {
        setErrorSummaryVisible(true);
        const validationResult = AddressModelWithoutAnalyticsSchema.safeParse(fields.value);
        if (!validationResult.success) {
            clearAllErrors();
            validationResult.error.issues.forEach((issue) => {
                const key = String(issue.path?.[0] ?? '');
                if (!key) return;
                setError(key, [...(errors.value[key] ?? []), issue.message]);
            });
        }
        let matchingCityResult;
        if (validationResult.success) {
            matchingCityResult = await validatePostalCodeAndCity(
                validationResult.data.postalCode,
                validationResult.data.city,
            );
            if (!matchingCityResult) return false;
            if (!validationResult.data.bringAddressId) {
                tryToGetAddressIdFromSuggestions(validationResult);
            }
        }
        if (!validationResult.success || matchingCityResult === USER_FEEDBACK.UNKNOWN_POSTALCODE) {
            return false;
        }
        try {
            const success = await updateCustomer(validationResult.data);
            if (!success) return false;
        } catch (error) {
            Log({
                LogLevel: 'Warning',
                Message: `[customerInformationStore] Failed to update customer information: ${error instanceof Error ? error.message : String(error)}`,
            });
            return false;
        }
        setSubmittedFields(validationResult.data);
        clearAllErrors();
        return true;
    }

    async function tryGetCityMatchingPostalCode(postalCode: string): Promise<string | undefined> {
        if (!postalCode) return;
        setRetrievingCity(true);
        const result = await getCityFromPostalCode(postalCode);
        setRetrievingCity(false);
        return result;
    }

    async function validatePostalCodeAndCity(postalCode: string, city: string): Promise<boolean | string> {
        const result = await getCityFromPostalCode(postalCode);
        if (result === USER_FEEDBACK.UNKNOWN_POSTALCODE) {
            setError('postalCode', [FEEDBACK_TEXT().UNKNOWN_POSTALCODE]);
            return false;
        }
        if (result !== city.toUpperCase()) {
            setError('postalCode', [FEEDBACK_TEXT().POSTALCODE_AND_CITY_DONT_MATCH]);
            return false;
        }
        return true;
    }

    function tryToGetAddressIdFromSuggestions(validationResult: any): void {
        const match = mostRecentAddressSuggestions.value.find(
            (suggestion) =>
                CheckoutHelpers.formatAddress(suggestion).toUpperCase() ===
                    validationResult.data.address.toUpperCase() &&
                suggestion.postalCode === validationResult.data.postalCode &&
                suggestion.city.toUpperCase() === validationResult.data.city.toUpperCase(),
        );
        if (match) {
            setField('bringAddressId', match.addressId);
            validationResult.data.bringAddressId = match.addressId;
        }
    }

    hydrateFromLocalStorage();

    return {
        fields, errors, submittedFields, retrievingCity, errorSummaryVisible, mostRecentAddressSuggestions,
        isCustomerStepCompleted, fieldsEmpty, errorsExist,
        persistDraftToLocalStorage, loadDraftFromLocalStorage, clearDraftFromLocalStorage,
        hasDraftInput, hydrateFieldsIfEmpty, setField, setFields, clearAllFields,
        setError, clearError, clearAllErrors, setSubmittedFields, setErrorSummaryVisible,
        setRetrievingCity, setMostRecentAddressSuggestions, clear,
        setFieldsIfLoggedIn, submitForm, tryGetCityMatchingPostalCode,
        validatePostalCodeAndCity, tryToGetAddressIdFromSuggestions,
    };
});

